import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'src/redux/store';
import Api from 'src/services/ApiService';
import {
  GetSalesResponse,
  GetSalesRequest,
  GetSalesComputation,
  GetSalesPaymentResponse
} from 'src/types';
import { CommonAxiosResponse } from 'src/types';
import dayjs from 'dayjs';
import { cleanDate } from 'src/utils/dateTimeUtils';

const getSalesNextPageController = new AbortController();

export const getSalesThunk = createAsyncThunk<
  CommonAxiosResponse<GetSalesResponse>,
  GetSalesRequest | undefined,
  { state: RootState }
>('sale/getSalesThunk', async (data, api) => {
  getSalesNextPageController.abort();
  let request = data ?? api.getState().sales.salesFilter;
  request = { ...request, action: 'list', page_number: 1 };
  let response: CommonAxiosResponse<GetSalesResponse> = await Api.get(
    '/sales/v3/get',
    request
  );
  if (response?.success) {
    response = {
      ...response,
      originalData: {
        items: response.originalData.data,
        meta: response.originalData.meta
      }
    };
  }
  if (!response.success) {
    throw new Error('Failed getSalesThunk');
  }
  return response;
});

export const getSalesNextPageThunk = createAsyncThunk<
  CommonAxiosResponse<GetSalesResponse>,
  undefined,
  { state: RootState }
>('sale/getSalesNextPageThunk', async (_, api) => {
  let request = api.getState().sales.salesFilter;
  let nextPage =
    (api.getState().sales.salesResponse?.meta?.current_page || 1) + 1;

  request = { ...request, action: 'list', page_number: nextPage };
  let response: CommonAxiosResponse<GetSalesResponse> = await Api.get(
    '/sales/v3/get',
    request,
    {
      signal: getSalesNextPageController.signal
    }
  );
  if (response?.success) {
    response = {
      ...response,
      originalData: {
        items: response.originalData.data,
        meta: response.originalData.meta
      }
    };
  }
  if (!response.success) {
    throw new Error('Failed getSalesNextPageThunk');
  }
  return response;
});

export const getSalesComputationThunk = createAsyncThunk<
  CommonAxiosResponse<GetSalesComputation>,
  GetSalesRequest | undefined,
  { state: RootState }
>('sale/getSalesComputationThunk', async (data, api) => {
  let request = data ?? api.getState().sales.salesFilter;
  request = { ...request, ...data, action: 'computation' };
  let response: CommonAxiosResponse<GetSalesComputation> = await Api.get(
    '/sales/v3/get',
    request
  );
  if (!response.success) {
    throw new Error('Failed getSalesComputationThunk');
  }
  return response;
});

export const getSalesComputationThunkLastMonth = createAsyncThunk<
  CommonAxiosResponse<GetSalesComputation>,
  GetSalesRequest | undefined,
  { state: RootState }
>('sale/getSalesComputationThunkLastMonth', async (data, api) => {
  let request: GetSalesRequest = data ?? api.getState().sales.salesFilter;
  let filter_from_sales = api.getState().sales.salesFilter;

  // keyof GetSalesRequest
  const dateFields = [
    'sold_date_from',
    'sold_date_to',
    'encoded_date_from',
    'encoded_date_to',
    'purchased_date_from',
    'purchased_date_to'
  ];

  if (!request?.is_preset) {
    dateFields.forEach((field: string) => {
      // @ts-ignore
      const value = request?.[field];
      if (value) {
        request = {
          ...request,
          [field]: dayjs(value)
            .subtract(1, 'month')
            .toISOString()
        };
      }
    });
  }

  request = {
    ...request,
    ecomm_transactions_only: filter_from_sales?.ecomm_transactions_only,
    with_voucher_only: filter_from_sales?.with_voucher_only,
    action: 'computation'
  };

  let response: CommonAxiosResponse<GetSalesComputation> = await Api.get(
    '/sales/v3/get',
    request
  );
  if (!response.success) {
    throw new Error('Failed getSalesComputationThunkLastMonth');
  }
  return response;
});

export const getSalesPaymentThunk = createAsyncThunk<
  CommonAxiosResponse<GetSalesPaymentResponse>,
  GetSalesRequest | undefined,
  { state: RootState }
>('sale/getSalesPaymentThunk', async (data, api) => {
  let filter = data ?? api.getState().sales.salesFilter;

  const request = {
    start_date: cleanDate(filter.sold_date_from || ''),
    end_date: cleanDate(filter.sold_date_to || ''),
    branch_ids: filter.branch_ids || [],
    payment_type: filter.payment_type || []
  };

  let response: CommonAxiosResponse<GetSalesPaymentResponse> = await Api.get(
    '/sales/payments/get',
    request
  );
  if (!response.success) {
    throw new Error('Failed getSalesPaymentThunk');
  }
  return response;
});
